<template>
	<div>
		<DialogHeader closable @cancel="$emit('close')" />
		<DialogBody :class="!isXS ? 'mt-4' : 'height-full'">
			<TripRequestHelpLoader v-if="isLoading" :is-x-s="isXS" />
			<TripRequestHelpInfo
				v-else-if="helpAlreadyRequested"
				icon="chat-lines"
				:title="$t('web.contact_modal.in_contact.title')"
				:description="$t('web.contact_modal.in_contact.description')"
				:button-text="$t('common.button.understood')"
				:is-x-s="isXS"
				@click="$emit('close')"
			/>
			<div
				v-else-if="retryCreation === undefined"
				class="d-flex flex-column gap-6"
				:class="isXS ? 'height-full mt-4' : ''"
			>
				<h3 class="text-title-m text-neutral-900">{{ $t('web.contact_modal.header.title') }}</h3>
				<p class="mt-2 text-body-xl text-neutral-900">
					{{ $t('web.contact_modal.header.description') }}
				</p>
				<TripLeadForm class="mt-4" v-model="leadForm" :form-errors="formErrors" />
				<PButton
					class="send-form-button"
					:class="isXS ? 'send-form-button-mobile' : ''"
					size="lg"
					@click="validateLead"
				>
					{{ $t('web.contact_modal.form.submit.button') }}
				</PButton>
			</div>
			<TripRequestHelpInfo
				v-else-if="retryCreation === false"
				icon="xlSuccessReverse"
				:title="$t('web.contact_modal.success.title')"
				:description="$t('web.contact_modal.success.description')"
				:button-text="$t('common.button.understood')"
				:is-x-s="isXS"
				@click="$emit('close')"
			/>
			<TripRequestHelpInfo
				v-else
				icon="rounded-exclamation-mark"
				icon-class="text-error-200"
				:title="$t('web.contact_modal.error.title')"
				:description="$t('web.contact_modal.error.description')"
				:button-text="$t('common.action.retry')"
				:is-x-s="isXS"
				@click="createLead"
			/>
		</DialogBody>
	</div>
</template>

<script setup lang="ts">
	import { LeadFormInfo, TripLeadActions } from '~/actions/trip/lead'
	import { ref, onMounted } from 'vue'
	import { Itinerary2 } from '~/models/Itinerary'
	import { Profile } from '~/models/Profile'
	import { SalesLead } from '~/models/SalesLead'
	import { TripLeadMetric } from '~/actions/metrics/tripLead'
	import { Destination2 } from '~/models/Destination'

	interface Props {
		isXS: boolean
		trip: Itinerary2
		profile: Profile
		utmCampaign?: string
	}
	const props = defineProps<Props>()
	defineEmits(['close'])
	const route = useRoute()
	const leadForm = ref<LeadFormInfo>({
		phonePrefix: '+34',
		phone: '',
		numTravelers: 1,
		budget: undefined,
	})
	const formErrors = ref<Record<keyof LeadFormInfo, string>>()
	const retryCreation = ref<boolean | undefined>()
	const isLoading = ref<boolean>(true)
	const helpAlreadyRequested = ref<boolean>()
	const lead = ref<SalesLead>()

	onMounted(async () => {
		lead.value = await TripLeadActions.getLead(props.trip.id!)
		helpAlreadyRequested.value = Boolean(lead.value.destinationId)
		isLoading.value = false
	})

	const validateLead = () => {
		formErrors.value = TripLeadActions.validateLeadForm(leadForm.value)
		const isValid = !hasErrors(formErrors.value)
		if (isValid) createLead()
	}

	const hasErrors = (errors: Record<string, string>) => Object.values(errors).some((error) => error)

	const createLead = async () => {
		const destination = props.trip.destination!
		isLoading.value = true
		const utmInfo = { utmMedium: 'planner', utmCampaign: props.utmCampaign }
		const createdLead = await TripLeadActions.createLead(
			destination.id,
			props.trip.id!,
			props.profile.id!,
			leadForm.value,
			utmInfo
		)
		sendAddEvent(destination)
		isLoading.value = false
		retryCreation.value = !createdLead.destinationId
	}

	const sendAddEvent = (destination: Destination2) => {
		const routeName: string = route.name?.toString() || ''
		TripLeadMetric.addLead(destination.standardizedName!, props.trip.id!, routeName)
	}
</script>
<style lang="scss" scoped>
	.send-form-button {
		width: 100%;
		&-mobile {
			margin-top: auto;
		}
	}
</style>
